import { inject, Injectable } from '@angular/core';
import { LangChangeEvent, TranslateService } from '@ngx-translate/core';
import { BehaviorSubject } from 'rxjs';
import { I18nFacade } from './i18n-facade';
import { LanguageOption } from './i18n.types';

@Injectable({ providedIn: 'root' })
export class NgxI18nFacadeService implements I18nFacade {
  private readonly translate = inject(TranslateService);
  private readonly storageKey = 'selectedLanguage';
  private readonly defaultLang = 'es';
  private readonly languages: LanguageOption[] = [
    {
      label: 'En',
      value: 'en',
      flag: 'https://www.shutterstock.com/image-vector/usa-america-set-circular-circle-600nw-2479083185.jpg',
    },
    {
      label: 'Es',
      value: 'es',
      flag: '/assets/images/spain-flag.png',
    },
  ];

  private readonly currentLanguageSubject = new BehaviorSubject<string>(this.defaultLang);
  readonly currentLanguage$ = this.currentLanguageSubject.asObservable();

  constructor() {
    const savedLang = localStorage.getItem(this.storageKey);
    const langToUse = savedLang || this.defaultLang;

    this.translate.setDefaultLang(this.defaultLang);
    this.translate.use(langToUse);
    this.currentLanguageSubject.next(langToUse);

    this.translate.onLangChange.subscribe((event: LangChangeEvent) => {
      this.currentLanguageSubject.next(event.lang || this.defaultLang);
    });
  }

  setLanguage(lang: string): void {
    localStorage.setItem(this.storageKey, lang);
    this.translate.use(lang);
    this.currentLanguageSubject.next(lang);
  }

  getCurrentLanguage(): string {
    return this.translate.currentLang || this.defaultLang;
  }

  getAvailableLanguages(): LanguageOption[] {
    return this.languages;
  }

  instant(key: string, params?: Record<string, unknown>): string {
    return this.translate.instant(key, params);
  }
}
